/**
 * Sprint 22 — SessionHistoryList.
 *
 * Full-width session history for the chat panel, shown when the header's history
 * toggle is on (it replaces the thread — the 380px rail is too narrow for a
 * permanent side column). Lists sessions for the current scope with an "All"
 * toggle (broadens to workspace sessions) and a title filter. Clicking a session
 * restores it (and closes history, handled by the panel).
 *
 * NOTE: v1 search is a client-side title filter. Semantic search over session
 * content (Q6) is deferred — chat sessions aren't vectorised yet (Sprint 22 only
 * adds the memory_source_chat_session_id link). See [[design-admin-chat-cockpit]].
 */

import { useMemo, useState, type CSSProperties } from "react";
import { Trash2, Search } from "@icons";
import { EmptyState, showToast } from "../primitives";
import { useChatRail, type ChatScope } from "./ChatRailContext";
import {
  useChatSessions,
  useArchiveChatSession,
  useDeleteChatSession,
  type ChatSession,
} from "../../hooks/use-chat-sessions";
import { DestructiveOpConfirmation, type PendingDestructiveOp } from "./DestructiveOpConfirmation";

interface SessionHistoryListProps {
  activeSessionId: string | null;
  onSelect: (sessionId: string) => void;
}

export function SessionHistoryList({ activeSessionId, onSelect }: SessionHistoryListProps) {
  const { scope } = useChatRail();
  const [showAll, setShowAll] = useState(false);
  const [query, setQuery] = useState("");
  const [pendingDelete, setPendingDelete] = useState<ChatSession | null>(null);

  const workspaceScope: ChatScope = { kind: "workspace", label: "This workspace" };
  const listScope = showAll ? workspaceScope : scope;
  const sessions = useChatSessions(listScope);
  const archive = useArchiveChatSession();
  const del = useDeleteChatSession();

  const filtered = useMemo(() => {
    const rows = sessions.data ?? [];
    const q = query.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((s) => s.title.toLowerCase().includes(q));
  }, [sessions.data, query]);

  return (
    <div style={panel}>
      <div style={controls}>
        <div style={searchRow}>
          <Search size={13} style={{ color: "var(--color-text-tertiary)", flexShrink: 0 }} />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Filter chats…"
            style={searchInput}
          />
        </div>
        <div style={toggleRow}>
          <button
            type="button"
            onClick={() => setShowAll(false)}
            style={{ ...toggleBtn, ...(!showAll ? toggleActive : null) }}
          >
            This scope
          </button>
          <button
            type="button"
            onClick={() => setShowAll(true)}
            style={{ ...toggleBtn, ...(showAll ? toggleActive : null) }}
          >
            All
          </button>
        </div>
      </div>

      <div style={list}>
        {sessions.isLoading ? (
          <div style={{ padding: 12, fontSize: 12, color: "var(--color-text-tertiary)" }}>Loading…</div>
        ) : filtered.length === 0 ? (
          // Sprint 23.9 (Fix 1 / S23.9.15) — distinguish "scope has no chats yet"
          // from "the filter matched nothing".
          <EmptyState>
            {query.trim()
              ? "No chats match your filter."
              : "No chats yet — your first conversation will appear here."}
          </EmptyState>
        ) : (
          filtered.map((s) => (
            <div
              key={s.id}
              style={{ ...row, ...(s.id === activeSessionId ? rowActive : null) }}
              onClick={() => onSelect(s.id)}
              role="button"
              tabIndex={0}
            >
              <span style={rowTitle}>{s.title || "(untitled)"}</span>
              <span style={rowActions}>
                <button
                  type="button"
                  title="Archive"
                  style={rowIconBtn}
                  onClick={(e) => {
                    e.stopPropagation();
                    archive.mutate(s.id, {
                      onSuccess: () => showToast("Chat archived", "success"),
                      onError: (err) => showToast(err instanceof Error ? err.message : "Archive failed", "error"),
                    });
                  }}
                >
                  <i className="ti ti-archive" style={{ fontSize: 13 }} aria-hidden="true" />
                </button>
                <button
                  type="button"
                  title="Delete"
                  style={rowIconBtn}
                  onClick={(e) => {
                    e.stopPropagation();
                    setPendingDelete(s);
                  }}
                >
                  <Trash2 size={13} />
                </button>
              </span>
            </div>
          ))
        )}
      </div>

      <DestructiveOpConfirmation
        op={
          pendingDelete
            ? ({
                toolName: "delete_chat_session",
                summary: `Permanently delete the chat "${pendingDelete.title || "(untitled)"}"? This can't be undone.`,
              } as PendingDestructiveOp)
            : null
        }
        onCancel={() => setPendingDelete(null)}
        onConfirm={async () => {
          if (!pendingDelete) return;
          try {
            await del.mutateAsync(pendingDelete.id);
            showToast("Chat deleted", "success");
          } catch (err) {
            showToast(err instanceof Error ? err.message : "Delete failed (owner only)", "error");
          } finally {
            setPendingDelete(null);
          }
        }}
      />
    </div>
  );
}

const panel: CSSProperties = {
  flex: "1 1 auto",
  minWidth: 0,
  display: "flex",
  flexDirection: "column",
  background: "var(--color-background-primary)",
  fontFamily: "var(--font-sans)",
  overflow: "hidden",
};

const controls: CSSProperties = {
  flex: "0 0 auto",
  padding: 8,
  display: "flex",
  flexDirection: "column",
  gap: 8,
  borderBottom: "var(--kuvio-border-default)",
};

const searchRow: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 6,
  padding: "5px 8px",
  borderRadius: "var(--border-radius-md)",
  background: "var(--color-background-secondary)",
  border: "var(--kuvio-border-default)",
};

const searchInput: CSSProperties = {
  flex: 1,
  minWidth: 0,
  border: "none",
  background: "transparent",
  outline: "none",
  fontSize: 13,
  color: "var(--color-text-primary)",
  fontFamily: "var(--font-sans)",
};

const toggleRow: CSSProperties = {
  display: "flex",
  gap: 4,
};

const toggleBtn: CSSProperties = {
  flex: 1,
  padding: "5px 8px",
  borderRadius: "var(--border-radius-sm)",
  border: "var(--kuvio-border-default)",
  background: "transparent",
  color: "var(--color-text-tertiary)",
  fontSize: 12,
  cursor: "pointer",
};

const toggleActive: CSSProperties = {
  background: "var(--color-background-secondary)",
  color: "var(--color-text-primary)",
};

const list: CSSProperties = {
  flex: "1 1 auto",
  overflowY: "auto",
  padding: "6px",
  display: "flex",
  flexDirection: "column",
  gap: 2,
};

const row: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 4,
  padding: "8px 10px",
  borderRadius: "var(--border-radius-sm)",
  cursor: "pointer",
  fontSize: 13,
  color: "var(--color-text-secondary)",
};

const rowActive: CSSProperties = {
  background: "var(--color-background-secondary)",
  color: "var(--color-text-primary)",
};

const rowTitle: CSSProperties = {
  flex: 1,
  minWidth: 0,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

const rowActions: CSSProperties = {
  display: "inline-flex",
  gap: 2,
  flexShrink: 0,
};

const rowIconBtn: CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  width: 24,
  height: 24,
  border: "none",
  background: "transparent",
  color: "var(--color-text-tertiary)",
  cursor: "pointer",
  borderRadius: "var(--border-radius-sm)",
};
